import React, { useEffect, useMemo, useState } from "react";
import { Modal, Button, Form, Badge, Alert } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";

import {
  addSancion,
  fetchSancionesByAfiliado,
} from "../../reducers/sancion_slice";
import { fetchAfiliados } from "../../reducers/afiliado_slice";
import CustomPrintModal from "../UI/CustomPrintModal";

// ✅ Para resolver URL del PDF (backend)
import { getUploadUrl } from "../../util/valueCalculator";

const SancionForm = ({ afiliado, onClose }) => {
  const dispatch = useDispatch();
  const sesion = useSelector((s) => s.sesion);
  const sancionesMap = useSelector((s) => s.sancion.byAfiliado || {});
  const logs = sancionesMap[afiliado?.id]; // { count, rows }

  const hoy = () => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  };

  const esVigenteHoy = (r) => {
    if (!r?.fecha_inicio) return false;
    const h = hoy().getTime();
    const ini = new Date(r.fecha_inicio);
    ini.setHours(0, 0, 0, 0);
    const iniT = ini.getTime();

    let finT = null;
    if (r.fecha_fin) {
      const fin = new Date(r.fecha_fin);
      fin.setHours(23, 59, 59, 999);
      finT = fin.getTime();
    }

    if (h < iniT) return false;
    if (finT !== null && h > finT) return false;
    return true;
  };

  const getEstadoActual = () => {
    const rows = logs?.rows ? logs.rows : [];
    return rows.some(esVigenteHoy) ? "CON SANCION" : "SIN SANCION";
  };

  const estadoActual = getEstadoActual();

  // ✅ Si está con sanción vigente, NO se muestra formulario de alta
  const bloqueoAlta = estadoActual === "CON SANCION";

  // =========================
  // ✅ FIX FECHAS (Bolivia -04:00)
  // =========================
  const ymdLocal = (d = new Date()) => {
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  };

  const toBoliviaISO = (ymd, endOfDay = false) => {
    if (!ymd) return "";
    return `${ymd}T${endOfDay ? "23:59:59" : "12:00:00"}-04:00`;
  };

  // Form state
  const [ambito, setAmbito] = useState("PROFESIONAL");
  const [detalleTipo, setDetalleTipo] = useState("");
  const [fechaImposicion, setFechaImposicion] = useState(ymdLocal());
  const [fechaInicio, setFechaInicio] = useState("");
  const [fechaFin, setFechaFin] = useState("");
  const [observacion, setObservacion] = useState("");
  const [resolucionFile, setResolucionFile] = useState(null);

  // UI states
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [successInfo, setSuccessInfo] = useState(null);

  // Orden historial
  const [sortDir, setSortDir] = useState("DESC");
  const [showPrint, setShowPrint] = useState(false);
  const [printContent, setPrintContent] = useState({ head: [], body: [] });

  // Vista previa (modal)
  const [preview, setPreview] = useState({ show: false, url: null, tipo: null });

  // Cargar historial
  useEffect(() => {
    if (afiliado?.id) {
      dispatch(fetchSancionesByAfiliado(afiliado.id));
    }
  }, [dispatch, afiliado?.id]);

  const sortedRows = useMemo(() => {
    const rows = logs?.rows ? [...logs.rows] : [];
    rows.sort((a, b) => {
      const da = new Date(a.createdAt).getTime();
      const db = new Date(b.createdAt).getTime();
      return sortDir === "ASC" ? da - db : db - da;
    });
    return rows;
  }, [logs?.rows, sortDir]);

  const toggleSort = () => setSortDir((d) => (d === "ASC" ? "DESC" : "ASC"));

  const getUsuarioLabel = (r) => {
    if (r?.Usuario?.usuario) return r.Usuario.usuario;
    if (r?.Usuario?.nombre) return r.Usuario.nombre;
    return r?.id_usuario || "—";
  };

  // ✅ PDF: resolver URL correctamente (backend)
  const handlePreviewResolucion = (r) => {
    if (!r?.resolucion) return;

    const raw = String(r.resolucion).trim();
    const isAbs = /^https?:\/\//i.test(raw);
    const urlFinal = isAbs ? raw : getUploadUrl(raw);

    setPreview({ show: true, url: urlFinal, tipo: r?.resolucion_tipo || null });
  };

  const buildRowsForPrint = () => {
    const head = [
      [
        "N°",
        "Fecha imposición",
        "Inicio",
        "Fin",
        "Tipo sanción",
        "Observación",
        "Usuario",
        "Estado",
      ],
    ];

    const body = sortedRows.map((r, idx) => [
      idx + 1,
      r?.fecha_imposicion ? new Date(r.fecha_imposicion).toLocaleDateString() : "—",
      r?.fecha_inicio ? new Date(r.fecha_inicio).toLocaleDateString() : "—",
      r?.fecha_fin ? new Date(r.fecha_fin).toLocaleDateString() : "—",
      r?.tipo_sancion || "—",
      r?.observacion || "—",
      getUsuarioLabel(r),
      r?.sancion_estado || "—",
    ]);

    return { head, body };
  };

  const handlePrintPreview = () => {
    setPrintContent(buildRowsForPrint());
    setShowPrint(true);
  };

  const handleGuardar = async () => {
    try {
      setSaving(true);
      setError("");

      if (!detalleTipo.trim()) {
        setError("Debe especificar el detalle del tipo de sanción.");
        setSaving(false);
        return;
      }
      if (!fechaInicio) {
        setError("Debe especificar la fecha de inicio de la sanción.");
        setSaving(false);
        return;
      }

      const tipo_sancion = `${ambito}: ${detalleTipo.trim()}`;

      const fd = new FormData();
      fd.append("id_afiliado", afiliado.id);
      fd.append("id_usuario", sesion?.id);

      fd.append("fecha_imposicion", toBoliviaISO(fechaImposicion));
      if (fechaInicio) fd.append("fecha_inicio", toBoliviaISO(fechaInicio));
      if (fechaFin) fd.append("fecha_fin", toBoliviaISO(fechaFin, true));

      if (tipo_sancion) fd.append("tipo_sancion", tipo_sancion);
      if (observacion) fd.append("observacion", observacion);
      if (resolucionFile) fd.append("resolucion", resolucionFile);

      await dispatch(addSancion(fd)).unwrap();

      await dispatch(fetchAfiliados());
      await dispatch(fetchSancionesByAfiliado(afiliado.id));

      setSuccessInfo({
        ambito,
        tipo: detalleTipo || null,
        fecha_imposicion: toBoliviaISO(fechaImposicion),
        fecha_inicio: fechaInicio ? toBoliviaISO(fechaInicio) : null,
        fecha_fin: fechaFin ? toBoliviaISO(fechaFin, true) : null,
      });
    } catch (e) {
      const msg = e?.error || e?.message || "Ocurrió un error al guardar la sanción";
      setError(msg);
      Swal.fire("Error", msg, "error");
    } finally {
      setSaving(false);
    }
  };

  const handleContinuar = () => onClose();

  const guardarDisabled = saving;

  return (
    <>
      <Modal show onHide={onClose} backdrop="static" size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>Gestionar sanción</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {successInfo ? (
            <div className="alert alert-success" role="alert">
              <h6 className="mb-2">¡Registro de sanción guardado correctamente!</h6>
              <div className="small">
                <div>
                  <strong>Afiliado:</strong> {afiliado?.persona?.apellido1} {afiliado?.persona?.apellido2},{" "}
                  {afiliado?.persona?.nombre} (Reg. <b>{afiliado?.num_reg || "—"}</b>)
                </div>
                <div className="mt-2">
                  <strong>Estado (hoy):</strong> {estadoActual}
                </div>
                <div>
                  <strong>Ámbito:</strong> {successInfo.ambito}
                </div>
                {successInfo.tipo && (
                  <div>
                    <strong>Detalle:</strong> {successInfo.tipo}
                  </div>
                )}
                <div>
                  <strong>Imposición:</strong>{" "}
                  {successInfo.fecha_imposicion
                    ? new Date(successInfo.fecha_imposicion).toLocaleDateString()
                    : "—"}
                </div>
                {successInfo.fecha_inicio && (
                  <div>
                    <strong>Inicio:</strong> {new Date(successInfo.fecha_inicio).toLocaleDateString()}
                  </div>
                )}
                {successInfo.fecha_fin && (
                  <div>
                    <strong>Fin:</strong> {new Date(successInfo.fecha_fin).toLocaleDateString()}
                  </div>
                )}
              </div>

              <div className="mt-3 d-flex justify-content-end">
                <Button variant="success" onClick={handleContinuar}>
                  Cerrar
                </Button>
              </div>
            </div>
          ) : (
            <>
              {error && <Alert variant="danger">{error}</Alert>}

              <div className="d-flex align-items-center justify-content-between mb-2">
                <div>
                  <div className="fw-bold">
                    {afiliado?.persona?.apellido1} {afiliado?.persona?.apellido2},{" "}
                    {afiliado?.persona?.nombre}
                  </div>
                  <div className="text-muted small">
                    Reg. <b>{afiliado?.num_reg || "—"}</b> — CI: {afiliado?.persona?.ci || "—"}
                  </div>
                </div>

                <div className="text-end">
                  <div className="small text-muted">Estado actual</div>
                  <Badge bg={estadoActual === "CON SANCION" ? "secondary" : "success"}>
                    {estadoActual}
                  </Badge>
                </div>
              </div>

              {/* ✅ Si está CON SANCION: NO mostrar formulario, solo mensaje */}
              {bloqueoAlta ? (
                <Alert variant="warning">
                  El afiliado tiene una <b>sanción vigente</b>. No se puede registrar otra sanción mientras esté vigente.
                </Alert>
              ) : (
                <Form>
                  <Form.Group className="mb-2">
                    <Form.Label>Fecha de imposición</Form.Label>
                    <Form.Control
                      type="date"
                      value={fechaImposicion}
                      onChange={(e) => setFechaImposicion(e.target.value)}
                      disabled={saving}
                    />
                  </Form.Group>

                  <Form.Group className="mb-2">
                    <Form.Label>Ámbito de sanción</Form.Label>
                    <Form.Select
                      value={ambito}
                      onChange={(e) => setAmbito(e.target.value)}
                      disabled={saving}
                    >
                      <option value="PROFESIONAL">PROFESIONAL</option>
                      <option value="INSTITUCIONAL">INSTITUCIONAL</option>
                    </Form.Select>
                  </Form.Group>

                  <Form.Group className="mb-2">
                    <Form.Label>Detalle del tipo de sanción</Form.Label>
                    <Form.Control
                      value={detalleTipo}
                      onChange={(e) => setDetalleTipo(e.target.value)}
                      placeholder="Ej: Suspensión por falta ética, etc."
                      disabled={saving}
                    />
                  </Form.Group>

                  <Form.Group className="mb-2">
                    <Form.Label>Fecha de inicio</Form.Label>
                    <Form.Control
                      type="date"
                      value={fechaInicio}
                      onChange={(e) => setFechaInicio(e.target.value)}
                      disabled={saving}
                    />
                  </Form.Group>

                  <Form.Group className="mb-2">
                    <Form.Label>Fecha de fin (opcional)</Form.Label>
                    <Form.Control
                      type="date"
                      value={fechaFin}
                      onChange={(e) => setFechaFin(e.target.value)}
                      disabled={saving}
                    />
                  </Form.Group>

                  <Form.Group className="mb-2">
                    <Form.Label>Observación</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      value={observacion}
                      onChange={(e) => setObservacion(e.target.value)}
                      disabled={saving}
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Resolución (archivo)</Form.Label>
                    <Form.Control
                      type="file"
                      onChange={(e) => setResolucionFile(e.target.files?.[0] || null)}
                      disabled={saving}
                    />
                  </Form.Group>

                  <div className="d-flex justify-content-end gap-2">
                    <Button variant="outline-secondary" onClick={onClose} disabled={saving}>
                      Cancelar
                    </Button>
                    <Button variant="primary" onClick={handleGuardar} disabled={guardarDisabled}>
                      {saving ? "Guardando..." : "Guardar"}
                    </Button>
                  </div>
                </Form>
              )}

              <hr className="my-3" />

              <div className="d-flex align-items-center justify-content-between">
                <h6 className="mb-0">Historial</h6>
                <div className="d-flex gap-2">
                  <Button variant="outline-secondary" size="sm" onClick={toggleSort}>
                    Orden: {sortDir}
                  </Button>
                  <Button variant="outline-primary" size="sm" onClick={handlePrintPreview}>
                    <i className="bi bi-printer"></i> Imprimir
                  </Button>
                </div>
              </div>

              <div className="table-responsive mt-2" style={{ maxHeight: "35vh", overflowY: "auto" }}>
                <table className="table table-sm table-striped table-hover">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Imposición</th>
                      <th>Inicio</th>
                      <th>Fin</th>
                      <th>Tipo sanción</th>
                      <th>Observación</th>
                      <th>Usuario</th>
                      <th>Estado</th>
                      <th>Resolución</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedRows.map((r, idx) => (
                      <tr key={r.id}>
                        <td>{idx + 1}</td>
                        <td>
                          {r?.fecha_imposicion
                            ? new Date(r.fecha_imposicion).toLocaleDateString()
                            : "—"}
                        </td>
                        <td>{r?.fecha_inicio ? new Date(r.fecha_inicio).toLocaleDateString() : "—"}</td>
                        <td>{r?.fecha_fin ? new Date(r.fecha_fin).toLocaleDateString() : "—"}</td>
                        <td>{r?.tipo_sancion || "—"}</td>
                        <td>{r?.observacion || "—"}</td>
                        <td>{getUsuarioLabel(r)}</td>
                        <td>{r?.sancion_estado || "—"}</td>
                        <td>
                          {r?.resolucion ? (
                            <Button
                              variant="outline-primary"
                              size="sm"
                              onClick={() => handlePreviewResolucion(r)}
                            >
                              Ver
                            </Button>
                          ) : (
                            <span className="text-muted">—</span>
                          )}
                        </td>
                      </tr>
                    ))}
                    {sortedRows.length === 0 && (
                      <tr>
                        <td colSpan="9" className="text-muted">
                          Sin registros.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </Modal.Body>
      </Modal>

      <CustomPrintModal
        show={showPrint}
        handleClose={() => setShowPrint(false)}
        title1="COMITÉ DE ÉTICA — Historial de Sanciones"
        title2={`Afiliado: ${afiliado?.persona?.apellido1 || ""} ${afiliado?.persona?.apellido2 || ""}, ${
          afiliado?.persona?.nombre || ""
        } (Reg. ${afiliado?.num_reg || "—"})`}
        content={printContent}
        copia={false}
      />

      <Modal show={preview.show} onHide={() => setPreview({ show: false, url: null, tipo: null })} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>Vista previa</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {preview.url ? (
            <div className="text-center">
              <a href={preview.url} target="_blank" rel="noreferrer">
                Abrir en nueva pestaña
              </a>
              <div className="mt-3">
                <iframe
                  title="preview"
                  src={preview.url}
                  style={{ width: "100%", height: "70vh", border: 0 }}
                />
              </div>
            </div>
          ) : (
            <div className="text-muted">Sin archivo.</div>
          )}
        </Modal.Body>
      </Modal>
    </>
  );
};

export default SancionForm;